import React, { useEffect, useState } from "react";
import { Check, ArrowRight, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";

const FALLBACK_PACKAGES = [
    {
        id: "spark",
        name: "Start",
        tagline: "Get moving without the overwhelm",
        price: 199,
        hours: "~3 hours/month",
        bullets: [
            "4 social posts / month (1/week)",
            "Access to our Canva template library",
            "Weekly \"one tip\" email — Mondays",
            "No lock-ins, cancel anytime",
        ],
    },
    {
        id: "grow",
        name: "Build",
        tagline: "Steady growth, done with you",
        price: 349,
        hours: "~5.5 hours/month",
        bullets: [
            "8 social posts / month (2/week)",
            "1 short-form reel / month",
            "Fortnightly 30-min check-in call",
            "Light DM triage (2× a week)",
            "Monthly one-page report",
        ],
    },
    {
        id: "flourish",
        name: "Scale",
        tagline: "Real momentum, without the agency price tag",
        price: 499,
        hours: "~8 hours/month",
        bullets: [
            "12 social posts / month (3/week)",
            "2 short-form reels / month",
            "Weekly 30-min check-in call",
            "Full DM & comment management",
            "Monthly report + next-month strategy",
            "Paid ads coaching (you run, I guide)",
        ],
    },
];

const FALLBACK = {
    label: "// Packages",
    title_prefix: "Pricing that",
    title_highlight: "respects your runway.",
    intro: "Three packages, month-to-month, no 12-month lock-ins. Prices are flat — no surprises, no upsells. Paid ad spend is separate (you keep the receipts).",
    packages: FALLBACK_PACKAGES,
    footnote: "Month-to-month · No 12-month lock-ins · Cancel any time after the first 60 days",
};

export default function Pricing() {
    const [hoverId, setHoverId] = useState(null);
    const [loadingId, setLoadingId] = useState(null);
    const [content, setContent] = useState(FALLBACK);

    useEffect(() => {
        api.get("/site-content/pricing")
            .then((r) => setContent({ ...FALLBACK, ...r.data.data }))
            .catch(() => {});
    }, []);

    function enquire(id) {
        try {
            const pkg = (content.packages || []).find((p) => p.id === id);
            sessionStorage.setItem("sd_package", pkg?.name || id);
        } catch (_) {
            /* ignore */
        }
        const el = document.getElementById("contact");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }

    async function bookNow(id) {
        setLoadingId(id);
        try {
            const origin = window.location.origin;
            const { data } = await api.post("/checkout/session", {
                package_id: id,
                currency: "NZD",
                origin_url: origin,
            });
            if (data?.url) {
                window.location.href = data.url;
            } else {
                toast.error("Couldn't open checkout — try enquire instead.");
                setLoadingId(null);
            }
        } catch (e) {
            toast.error(
                formatApiErrorDetail(e.response?.data?.detail) ||
                    "Couldn't open checkout — try enquire instead.",
            );
            setLoadingId(null);
        }
    }

    return (
        <section
            id="pricing"
            className="relative border-b border-hairline bg-sage py-24 lg:py-36"
            data-testid="pricing-section"
        >
            <div className="mx-auto max-w-[1400px] px-5 lg:px-10">
                <div className="mb-12 flex flex-col gap-6 lg:mb-16 lg:flex-row lg:items-end lg:justify-between">
                    <div>
                        <div className="mb-4 text-xs font-bold uppercase tracking-[0.22em] text-coral">
                            {content.label}
                        </div>
                        <h2 className="font-display text-4xl font-black leading-[0.98] tracking-tight text-ink sm:text-5xl lg:text-6xl">
                            {content.title_prefix}{" "}
                            <span className="font-serif italic font-normal text-coral">
                                {content.title_highlight}
                            </span>
                        </h2>
                    </div>
                </div>

                <p className="mb-10 max-w-2xl text-sm text-ink/65 lg:text-base">
                    {content.intro}
                </p>

                <div className="grid gap-5 lg:grid-cols-3">
                    {(content.packages || []).map((copy) => {
                        const id = copy.id;
                        const highlighted = id === "grow";
                        return (
                            <article
                                key={id}
                                onMouseEnter={() => setHoverId(id)}
                                onMouseLeave={() => setHoverId(null)}
                                className={`relative flex flex-col border-2 p-7 transition-all hover:-translate-y-1 lg:p-9 ${
                                    highlighted
                                        ? "border-coral bg-coral text-white lg:-mt-6 lg:frame"
                                        : "border-ink/20 bg-white text-ink hover:border-coral hover:frame-coral"
                                }`}
                                data-testid={`pricing-card-${id}`}
                            >
                                {highlighted && (
                                    <span className="absolute -top-3 left-7 border-2 border-ink bg-sage px-3 py-1 font-display text-[10px] font-black uppercase tracking-widest text-coral">
                                        Most Popular
                                    </span>
                                )}

                                <div
                                    className={`flex items-center justify-between border-b pb-5 ${
                                        highlighted ? "border-white/25" : "border-ink/15"
                                    }`}
                                >
                                    <div>
                                        <div className="font-display text-3xl font-black uppercase tracking-tight lg:text-4xl">
                                            {copy.name}
                                        </div>
                                        <div
                                            className={`mt-1 text-xs uppercase tracking-widest ${
                                                highlighted ? "text-white/80" : "text-ink/55"
                                            }`}
                                        >
                                            {copy.tagline}
                                        </div>
                                    </div>
                                </div>

                                <div
                                    className="mt-7 flex items-baseline gap-2"
                                    data-testid={`pricing-price-${id}`}
                                >
                                    <span className="font-display text-6xl font-black tracking-tight lg:text-7xl">
                                        ${copy.price}
                                    </span>
                                    <div
                                        className={`flex flex-col text-xs font-bold uppercase leading-tight tracking-widest ${
                                            highlighted ? "text-white/80" : "text-ink/60"
                                        }`}
                                    >
                                        <span>/mo</span>
                                        <span>+ GST</span>
                                    </div>
                                </div>

                                <ul className="mt-7 grid gap-3">
                                    {copy.bullets.map((b) => (
                                        <li
                                            key={b}
                                            className={`flex items-start gap-3 text-sm leading-snug ${
                                                highlighted ? "text-white" : "text-ink"
                                            }`}
                                        >
                                            <span
                                                className={`mt-0.5 grid h-5 w-5 shrink-0 place-items-center border ${
                                                    highlighted
                                                        ? "border-white bg-sage text-coral"
                                                        : "border-coral text-coral"
                                                }`}
                                            >
                                                <Check className="h-3 w-3" strokeWidth={3} />
                                            </span>
                                            <span>{b}</span>
                                        </li>
                                    ))}
                                </ul>

                                <div className="mt-auto flex flex-col gap-3 pt-9">
                                    <button
                                        onClick={() => bookNow(id)}
                                        onMouseEnter={() => setHoverId(id)}
                                        onMouseLeave={() => setHoverId(null)}
                                        disabled={loadingId === id}
                                        className={`inline-flex items-center justify-between gap-3 border-2 px-5 py-4 font-display text-sm font-bold uppercase tracking-wider transition-all hover:-translate-y-0.5 disabled:opacity-60 ${
                                            highlighted
                                                ? "border-white bg-white text-coral hover:bg-page"
                                                : "border-coral bg-coral text-white hover:bg-coral-hover"
                                        }`}
                                        data-testid={`pricing-book-${id}`}
                                        aria-label={`Book the ${copy.name} package now`}
                                    >
                                        {loadingId === id ? (
                                            <>
                                                Opening checkout…
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                            </>
                                        ) : (
                                            <>
                                                <span>Book {copy.name} now</span>
                                                <ArrowRight
                                                    className={`h-4 w-4 transition-transform ${
                                                        hoverId === id ? "translate-x-1" : ""
                                                    }`}
                                                />
                                            </>
                                        )}
                                    </button>
                                    <button
                                        onClick={() => enquire(id)}
                                        className={`text-center text-[11px] font-bold uppercase tracking-[0.22em] underline underline-offset-4 transition-colors ${
                                            highlighted
                                                ? "text-white/85 hover:text-white"
                                                : "text-ink/60 hover:text-coral"
                                        }`}
                                        data-testid={`pricing-cta-${id}`}
                                    >
                                        Not ready yet? Enquire first
                                    </button>
                                </div>
                            </article>
                        );
                    })}
                </div>

                <div className="mt-8 text-center text-xs uppercase tracking-widest text-ink/55">
                    {content.footnote}
                </div>
            </div>
        </section>
    );
}
